import { useMemo } from 'react';

// F29 — the window "since your last visit" is measured against.
//
// localStorage, not Firestore. Firestore would buy cross-device correctness and
// cost a schema field, periodic writes and a rules review; the failure mode it
// prevents (the banner appears on your phone, so your laptop never shows it) is
// arguably worse than the one it introduces, where each device honestly reports
// what changed since *it* last showed you.

const DAY_MS = 1000 * 60 * 60 * 24;

// Demo mode used to synthesise a fixed 23-day absence here, so F29 would
// demonstrate itself on every visit. Removed 2026-09-07 (owner: "too
// aggressive"): the overlay was the first thing it put in front of a visitor
// who had only just arrived, reporting a return from an absence that never
// happened. A demo has no history to measure against, so it reports none —
// which is also what FEATURES.md has always claimed F29 does here.

export const lastSeenKey = (userId) => `fiwara_lastSeenAt_${userId}`;

// --- test switch: pretend the user has been away -------------------------
//
// `null` in normal operation. Set it to a number of days to make the banner
// appear on every load, with a window that long, for whatever account you are
// signed into — useful for looking at the thing without waiting a month, and for
// checking a specific window length — 30.4375 is exactly one month as the app
// measures months, where the cash flow side should match the dashboard's
// cashflow widget row for row.
//
// Two safeguards, because a forced banner reaching real users would be a lie
// about their own money:
//   * development only — `npm start` honours it, production builds and the test
//     suite never do, so the real logic stays the thing under test;
//   * it never reads or writes localStorage, so a genuine `lastSeenAt` is left
//     untouched and switching back restores the real window intact.
export const FORCE_AWAY_DAYS = null;

const forcedAbsenceMs = (nowMs) => {
    if (FORCE_AWAY_DAYS == null) return null;
    if (process.env.NODE_ENV !== 'development') return null;
    return nowMs - FORCE_AWAY_DAYS * DAY_MS;
};

// Captured once per user per session. React StrictMode double-invokes state
// initializers and effects in development, and this is a read-then-write: run
// it twice and the second read returns the timestamp the first one just wrote,
// collapsing the window to milliseconds. Holding the captured value outside
// React makes the double-invoke harmless — and matches what the value means,
// which is "when this session started", not "current component state".
const capturedByUser = new Map();

// So the override announces itself once rather than on every render.
let warnedAboutForcing = false;

/**
 * The previous visit's timestamp in epoch millis, or null when there isn't a
 * usable one — first visit, cleared storage, incognito, or a stored value in
 * the future. Advances the stored timestamp to `nowMs` as a side effect.
 *
 * Order is load-bearing: read first, then write. Writing first destroys the
 * window this whole feature is about.
 */
export const captureLastSeenAt = (userId, nowMs, { isDemoMode = false } = {}) => {
    if (!userId) return null;

    const forced = forcedAbsenceMs(nowMs);
    if (forced !== null) {
        // Deliberately outside the session cache and the write: every load gets
        // a fresh forced window, and real storage is never touched.
        //
        // Checked BEFORE the demo branch below, on purpose: the override exists
        // so a developer can look at the banner without waiting a month, and
        // the demo portfolio is the most convenient place to look at it.
        if (!warnedAboutForcing) {
            warnedAboutForcing = true;
            // eslint-disable-next-line no-console
            console.warn(`[F29] Forcing a ${Number(FORCE_AWAY_DAYS).toFixed(4)}-day absence — test override, see useLastSeenAt.js`);
        }
        return forced;
    }

    // No stored history, and none invented: the demo shows no away banner.
    if (isDemoMode) return null;

    if (capturedByUser.has(userId)) return capturedByUser.get(userId);

    let previous = null;

    try {
        const stored = localStorage.getItem(lastSeenKey(userId));
        const parsed = stored == null ? NaN : Number(stored);
        // A value in the future means a clock that moved; treat it as a first
        // visit rather than reporting a negative window.
        previous = Number.isFinite(parsed) && parsed > 0 && parsed <= nowMs ? parsed : null;
        localStorage.setItem(lastSeenKey(userId), String(nowMs));
    } catch {
        previous = null; // storage blocked or full — no banner, no crash
    }

    capturedByUser.set(userId, previous);
    return previous;
};

/** Test seam: forget what this session captured. */
export const resetCapturedLastSeenAt = () => capturedByUser.clear();

/**
 * `now` should be the session clock App.js already pins for F28, so the two
 * banners can never disagree about the present.
 */
export const useLastSeenAt = (userId, isDemoMode, now) => useMemo(
    () => captureLastSeenAt(userId, now.getTime(), { isDemoMode }),
    [userId, isDemoMode, now],
);
